// Utility functions for the EnergyTrades.ai UI

// Format date/time for display
export function formatDateTime(dateString) {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    return date.toLocaleString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
}

// Format date for input fields (ISO format)
export function formatDateForInput(date = new Date()) {
  return date.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
}

// Format numbers for display
export function formatNumber(value, decimals = 2) {
  if (value == null || value === '') return '';

  const num = parseFloat(value);
  if (isNaN(num)) return value;

  return num.toLocaleString('it-IT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

// Format currency (EUR/MWh)
export function formatCurrency(value, decimals = 2) {
  if (value == null || value === '') return '';

  const num = parseFloat(value);
  if (isNaN(num)) return value;

  return `€${num.toLocaleString('it-IT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })}`;
}

// Format energy (MW/MWh)
export function formatEnergy(value, unit = 'MW', decimals = 1) {
  if (value == null || value === '') return '';

  const num = parseFloat(value);
  if (isNaN(num)) return value;

  return `${num.toLocaleString('it-IT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })} ${unit}`;
}

// Validate form data
export function validateAvailabilityForm(formData) {
  const errors = [];

  if (!formData.uvamId || formData.uvamId.trim() === '') {
    errors.push('UVAM ID è richiesto');
  }

  if (!formData.capacityMW || parseFloat(formData.capacityMW) <= 0) {
    errors.push('Capacità MW deve essere maggiore di 0');
  }

  if (!formData.startTime) {
    errors.push('Ora di inizio è richiesta');
  }

  if (!formData.durationMin || parseInt(formData.durationMin) <= 0) {
    errors.push('Durata deve essere maggiore di 0 minuti');
  }

  // Validate start time is in the future
  if (formData.startTime) {
    const startDate = new Date(formData.startTime);
    const now = new Date();
    if (startDate <= now) {
      errors.push('L\'ora di inizio deve essere nel futuro');
    }
  }

  return errors;
}

// Show notification with Material-UI styling
export function showNotification(message, type = 'info', duration = 3000) {
  // Remove existing notifications with animation
  const existing = document.querySelectorAll('.notification');
  existing.forEach(n => {
    n.style.animation = 'slideOut 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards';
    setTimeout(() => n.remove(), 200);
  });

  const notification = document.createElement('div');
  notification.className = `notification ${type}`;
  notification.textContent = message;

  // Add click to dismiss
  notification.addEventListener('click', () => {
    notification.style.animation = 'slideOut 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards';
    setTimeout(() => {
      if (notification.parentNode) {
        notification.remove();
      }
    }, 200);
  });

  document.body.appendChild(notification);

  // Auto-remove after duration
  setTimeout(() => {
    if (notification.parentNode) {
      notification.style.animation = 'slideOut 0.2s cubic-bezier(0.4, 0, 0.2, 1) forwards';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 200);
    }
  }, duration);
}

// Generate unique ID
export function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

// Debounce function
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Storage utilities
export const storage = {
  set(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  },

  get(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
      return defaultValue;
    }
  },

  remove(key) {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to remove from localStorage:', error);
    }
  },

  clear() {
    try {
      localStorage.clear();
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }
};

// DOM utilities
export function createElement(tag, className = '', textContent = '') {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (textContent) element.textContent = textContent;
  return element;
}

export function clearElement(element) {
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
}

// Event type mappings for display
export const EVENT_TYPE_LABELS = {
  'offer_created': 'Offerta Creata',
  'availability_created': 'Disponibilità Pubblicata',
  'activation_acknowledged': 'Attivazione Confermata',
  'cbp_event': 'Evento CBP',
  'telemetry_received': 'Telemetria Ricevuta'
};

// Status mappings
export const ACTIVATION_STATUS_LABELS = {
  'RECEIVED': 'Ricevuto',
  'REJECTED': 'Rifiutato',
  'EXECUTING': 'In Esecuzione',
  'COMPLETED': 'Completato'
};

export const SIDE_LABELS = {
  'BUY': 'Acquisto',
  'SELL': 'Vendita'
};

// Validation patterns
export const PATTERNS = {
  uvamId: /^[A-Z0-9]{3,10}$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/
};
